import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faBellSlash, faCircleDollar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getMessageStats } from 'api/engage';
import { StatusBadge } from 'assets/js/pages/engage/components/StatusBadge';
import { useReactTableState } from 'components/table';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { first, isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { EngageDelivery, EngageMessage } from 'types/domain';

const columnHelper = createColumnHelper<EngageDelivery>();

interface MessageDeliveryStatsProps {
    message: Partial<EngageMessage>;
}

const MessageDeliveryStats = ({ message }: MessageDeliveryStatsProps) => {
    const { accountId } = useCurrentAccount();

    const filterOptions = useMemo(() => {
        if (message.channel === 'sms') {
            return [
                { value: 'all', label: 'All' },
                { value: 'clicked', label: 'Clicked' },
                { value: 'supported', label: 'Supported' },
                { value: 'unsubscribed', label: 'Unsubscribed' },
            ];
        }
        return [
            { value: 'all', label: 'All' },
            { value: 'opened', label: 'Opened' },
            { value: 'bounced', label: 'Bounced' },
            { value: 'unopened', label: 'Unopened' },
            { value: 'clicked', label: 'Clicked' },
            { value: 'unsubscribed', label: 'Unsubscribed' },
            { value: 'supported', label: 'Supported' },
        ];
    }, [message.channel]);

    const [filterBy, setFilterBy] = useState<string>(first(filterOptions)?.value!);

    const { pagination, setPagination } = useReactTableState({
        pageSize: 10,
    });

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            filterBy,
        },
        500,
    );

    const { data: messageStats } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageStatsWithFilters(accountId, message.id!, params),
        queryFn: () => getMessageStats(accountId, message.id!, params),
    });

    const columns = useMemo(() => {
        const { channel } = message;

        return [
            columnHelper.display({
                id: 'name',
                header: 'Name',
                cell: info => {
                    const { id, first_name, last_name } = info.row.original.deliverable || {};
                    const name = first_name ? `${first_name} ${last_name}` : 'N/A';

                    if (id) {
                        return (
                            <Link
                                className="flex text-neutral-900"
                                to={`/accounts/${accountId}/contacts/${id}`}
                            >
                                {name}
                            </Link>
                        );
                    }

                    return <div className="flex text-gray-500">{name}</div>;
                },
            }),
            columnHelper.display({
                id: channel === 'email' ? 'email' : 'phone',
                header: channel === 'email' ? 'Email' : 'Number',
                cell: info => {
                    const { recipient } = info.row.original || {};

                    return (
                        <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                            {recipient ? (
                                <a
                                    className="flex text-neutral-900"
                                    href={
                                        channel === 'email'
                                            ? `mailto:${recipient}`
                                            : `tel:${recipient}`
                                    }
                                >
                                    {recipient}
                                </a>
                            ) : (
                                <EmptyData />
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'status',
                header: 'Status',
                cell: info => {
                    return (
                        <div className="flex w-full items-center justify-between">
                            <div className="flex items-center justify-center">
                                <StatusBadge row={info.row.original} channel={message.channel} />
                            </div>

                            <div className="grid grid-cols-2 gap-x-2">
                                {info.row.original.revenue_generated > 0 && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <FontAwesomeIcon
                                                className="h-4 w-4 rounded-full bg-green-100 p-1.5 text-green-600"
                                                icon={faCircleDollar}
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Content inverse side="left">
                                            <p>
                                                $
                                                {maskDecimal(
                                                    info.row.original.revenue_generated / 100,
                                                ) || 0}
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Root>
                                )}

                                {!!info.row.original.unsubscribed_at && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <FontAwesomeIcon
                                                className="h-4 w-4 rounded-full bg-red-100 p-1.5 text-red-600"
                                                icon={faBellSlash}
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Content inverse side="left">
                                            <p>
                                                Unsubscribed on{' '}
                                                {DateTime.fromISO(
                                                    info.row.original.unsubscribed_at,
                                                ).toFormat('MMM d, yyyy')}
                                            </p>
                                            <Tooltip.Arrow inverse />
                                        </Tooltip.Content>
                                    </Tooltip.Root>
                                )}
                            </div>
                        </div>
                    );
                },
            }),
        ];
    }, [accountId, message]);

    const table = useReactTable({
        data: messageStats?.data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        pageCount: messageStats?.meta.last_page ?? -1,
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, messageStats);

    const getFilterOption = (value: string) => filterOptions.find(item => item.value === value);

    return (
        <>
            <div className="mb-8 flex items-center justify-between">
                <div className="block text-xl font-medium">
                    {maskWholeNumber(messageStats?.meta.total ?? 0)}{' '}
                    {messageStats?.meta.total === 1 ? 'contact' : 'contacts'}
                </div>
                {!(
                    messageStats?.data.length === 0 &&
                    isEqual(filterBy, first(filterOptions)?.value)
                ) && (
                    <SingleSelect.Root value={filterBy} onValueChange={setFilterBy}>
                        <SingleSelect.Trigger className="w-72">
                            <SingleSelect.Value>
                                <span className="flex gap-1">
                                    <span className="font-medium">Showing:</span>
                                    {getFilterOption(filterBy)?.label}
                                </span>
                            </SingleSelect.Value>
                        </SingleSelect.Trigger>
                        <SingleSelect.Content>
                            {filterOptions.map(item => (
                                <SingleSelect.Item key={item.value} value={item.value}>
                                    <SingleSelect.ItemText>{item.label}</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            ))}
                        </SingleSelect.Content>
                    </SingleSelect.Root>
                )}
            </div>
            <Table.Root table={table} variant="bordered">
                <div className="flex flex-col gap-4">
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
                {!isPending && (
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>No contacts found</Table.EmptyTitle>
                    </Table.Empty>
                )}
            </Table.Root>
        </>
    );
};

export { MessageDeliveryStats };
